import type { TenantPlanOption } from "@/services/authService";

/**
 * What a plan costs THIS tenant, as opposed to what it costs in the catalog.
 *
 * The two diverge for Art. 5 tenants (Literal E / IVA mínimo) holding a current
 * Decreto 206/019 declaration: DGI requires the provider to discount the 80 UI
 * credit off the price, and the backend does so at the charge layer. Reading
 * `priceMonthly` on those surfaces would quote a number we do not charge — and
 * worse, `price === 0` is what several screens use to route between the free
 * activation path and checkout, so a stale price picks the wrong path outright.
 *
 * Every price shown to a user should come through here. `priceMonthly` /
 * `priceAnnual` remain readable, but only as the struck-through "antes" figure.
 */

/** The minimum shape these helpers need — both `/tenants/current/plans` rows and the `plan` object on `/tenants/current` satisfy it. */
export interface TenantPricedPlan {
  priceMonthly: number;
  priceAnnual: number;
  tenantPriceMonthly: number;
  tenantPriceAnnual: number | null;
  fiscalBenefitDiscount: number;
}

export type BillingIntervalUi = "monthly" | "annual";

/** What this tenant is charged, sin IVA. */
export function tenantPlanPrice(
  plan: TenantPricedPlan,
  interval: BillingIntervalUi,
): number {
  return interval === "annual"
    ? (plan.tenantPriceAnnual ?? plan.priceAnnual)
    : plan.tenantPriceMonthly;
}

/** The catalog price, for striking through beside a discounted one. */
export function catalogPlanPrice(
  plan: TenantPricedPlan,
  interval: BillingIntervalUi,
): number {
  return interval === "annual" ? plan.priceAnnual : plan.priceMonthly;
}

/**
 * Whether this tenant pays nothing for this plan on this cycle.
 *
 * PRICE-BASED on the TENANT price, deliberately. A benefit tenant on Tala pays
 * $0 even though Tala is a billable $513 tier, and the backend then activates
 * them directly with no dLocal subscription — because dLocal rejects any amount
 * below 1, so there is no checkout to complete. Screens that route on this must
 * send them down the free-activation path or the request is rejected.
 */
export function isTenantPlanFree(
  plan: TenantPricedPlan,
  interval: BillingIntervalUi,
): boolean {
  return tenantPlanPrice(plan, interval) === 0;
}

/** Whether this tenant's price is below the catalog price on this cycle. */
export function isTenantPlanDiscounted(
  plan: TenantPricedPlan,
  interval: BillingIntervalUi,
): boolean {
  return tenantPlanPrice(plan, interval) < catalogPlanPrice(plan, interval);
}

/**
 * Whether annual billing may be offered at all.
 *
 * The 80 UI credit accrues per month, so a tenant receiving it is billed monthly
 * only and the server returns `tenantPriceAnnual = null` for their billable
 * tiers. Screens must hide the annual toggle rather than offer a cycle the
 * checkout will reject.
 */
export function isAnnualOfferedToTenant(
  plans: readonly TenantPlanOption[],
): boolean {
  return plans.length > 0 && plans.every((p) => p.tenantPriceAnnual !== null);
}
